import { clearGridOrderIfSet } from "./repo";
/**
 * Leaving a ledger: signing out, and switching accounts.
 *
 * **The account is the home of the ledger; this device holds a copy.** So
 * signing out is "upload, then clear": anything pending is pushed first, and
 * the wipe — which is deliberate, so a shared device does not leave one
 * person's spending readable to the next — only ever destroys rows the server
 * already has. Sign-out blocks only when it cannot upload (offline, the session
 * is dead, or the push failed) with changes still pending, and then the caller
 * offers Try again, Cancel, and — separately, and marked destructive — Sign out
 * and discard N changes (SPEC §11.1).
 *
 * This lives in `lib/` rather than in the hook because the ordering matters and
 * ordering is testable: count, upload, count again, and clear last — clearing
 * the local data whether or not the server acknowledged the logout. The hook
 * keeps only the React state, and the sheets that ask the question live in
 * `features/onboarding/leave_sheets.tsx`. There is no `window.confirm` anywhere
 * on this path any more.
 *
 * Sign-out is not capture, so awaiting the network here does not touch INV-4:
 * the numpad still never waits on anything.
 */

import { finishSignOut, markSignedOut, signOutPending } from "./sign_out_marker";

import { importOutboxEntries } from "./outbox";
import { bridge } from "@/native/impl";

import { type Session, leaveFamilyMembership, readSession, signOut as signOutOfBackend } from "./backend";
import { clearLocalData, clearDatabase, db, familyDb, nowIso, getMeta, localLedgerAccountId, setMeta } from "./db";
import { familyLedger } from "./ledger";
import { FALLBACK_CATEGORY, addCategory, nextUpdatedAt, requireUser } from "./repo";
import type { LocalExpense } from "./types";
import { disableAutoSelect } from "./google_identity";
import { countPending, suspendFamilySync, waitForFamilySync, FAMILY_VIEW_KEY, FAMILY_ID_KEY, FAMILY_REMOVED_KEY, getFamilySyncStatus, getSyncStatus, resetFamilySyncStatus, queueSync, resetSyncStatus, sync } from "./sync";

/** Empty both device stores; a failed native wipe rejects and keeps the durable marker (INV-7). */
async function clearDevice(): Promise<void> {
  markSignedOut();
  await bridge.markSignedOut();
  await bridge.scheduleNotifications([]);
  await bridge.writeSnapshot({ signedOut: true });
  await bridge.clearAll();
  await clearLocalData();
  resetSyncStatus();
  finishSignOut();
}

/** Set while a sign-out is in progress; removed by the wipe that completes it. */
const SIGNED_OUT_KEY = "session.signedOut";

/**
 * The email of the account this device's ledger belongs to — a label for the
 * account-switch sheet ("Sign in as {previous} to upload"), never an identity.
 * Lives in `meta`, so `clearLocalData` wipes it with the ledger it describes.
 */
export const LEDGER_OWNER_EMAIL_KEY = "ledger.ownerEmail";

/**
 * How long sign-out waits for the pending rows to upload before it gives up
 * and asks. Long enough for a slow mobile network to push a day's rows; short
 * enough that a dead connection does not leave "Sign out" spinning (C-2).
 */
export const SIGN_OUT_UPLOAD_TIMEOUT_MS = 15_000;

/**
 * At most this many sync passes per sign-out attempt. A second pass catches a
 * row written while the first push was in flight; a third would mean the user
 * is still logging while signing out, and the sheet is the honest answer then.
 */
const MAX_UPLOAD_PASSES = 2;

/** Why the rows could not leave: decides the sheet's copy and its first action. */
export type LeaveBlockReason = "offline" | "expired" | "failed" | "family-unknown";

export type LeaveOutcome =
  | { kind: "clear" }
  | { kind: "blocked"; pending: number; reason: LeaveBlockReason };

/** The collaborators `prepareToLeave` needs, injectable so its branches are testable. */
export interface LeaveDeps {
  countPending: () => Promise<number>;
  sync: () => Promise<void>;
  syncState: () => string;
  hasSession: () => boolean;
  isOnline: () => boolean;
  timeoutMs: number;
}

const defaultLeaveDeps: LeaveDeps = {
  countPending: countAllPending,
  sync,
  syncState: () => getSyncStatus().state,
  hasSession: () => readSession() !== null,
  isOnline: () => typeof navigator === "undefined" || navigator.onLine,
  timeoutMs: SIGN_OUT_UPLOAD_TIMEOUT_MS,
};

/** Resolves `true` when `work` settles, `false` if `ms` passes first. */
async function settlesWithin(work: Promise<void>, ms: number): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      resolve(false);
    }, ms);
  });
  try {
    return await Promise.race([work.then(() => true), deadline]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Get this device ready to be emptied: upload what is pending, and say whether
 * anything is still at stake. **Changes nothing on the device** — the caller
 * runs `endSession()` on `clear`, or shows the leave sheet on `blocked`.
 *
 * `sync()` reports rather than throws (INV-7 lives in its status), so the
 * verdict is read from the count afterwards, and the reason from the status: a
 * 401 leaves it `signed-out`, a dropped connection `offline`, anything else —
 * a 5xx, a timeout — is `failed`.
 */
export async function prepareToLeave(deps: LeaveDeps = defaultLeaveDeps): Promise<LeaveOutcome> {
  let pending: number;
  try { pending = await deps.countPending(); }
  catch (cause) {
    if (cause instanceof UnknownFamilyPending) return { kind: "blocked", pending: cause.personalPending, reason: "family-unknown" };
    throw cause;
  }
  if (pending === 0) return { kind: "clear" };

  // Without a token there is no way to upload at all — and reconnecting will
  // not change that, so "offline" would be the wrong thing to say.
  if (!deps.hasSession()) return { kind: "blocked", pending, reason: "expired" };
  if (!deps.isOnline()) return { kind: "blocked", pending, reason: "offline" };

  for (let pass = 0; pass < MAX_UPLOAD_PASSES; pass += 1) {
    const settled = await settlesWithin(deps.sync(), deps.timeoutMs);
    try { pending = await deps.countPending(); }
    catch (cause) {
      if (cause instanceof UnknownFamilyPending) return { kind: "blocked", pending: cause.personalPending, reason: "family-unknown" };
      throw cause;
    }
    if (pending === 0) return { kind: "clear" };
    if (!settled) return { kind: "blocked", pending, reason: "failed" };

    const state = deps.syncState();
    if (state === "signed-out") return { kind: "blocked", pending, reason: "expired" };
    if (state === "offline") return { kind: "blocked", pending, reason: "offline" };
    // Only a clean pass earns another: rows remain because they were written
    // while the push was in flight. Any other state is a failure to report.
    if (state !== "idle") return { kind: "blocked", pending, reason: "failed" };
  }
  return { kind: "blocked", pending, reason: "failed" };
}

/**
 * Sign out and wipe this device — unconditionally.
 *
 * There is no prompt here any more: the decision was made before this was
 * called, by `prepareToLeave` returning `clear` or by the user choosing "Sign
 * out and discard N changes" on the leave sheet. Anything still pending at this
 * point is destroyed, and that was the user's informed choice.
 */
export async function endSession(): Promise<void> {
  // Stop Google from silently re-authenticating on the next load; otherwise
  // "sign out" would visibly undo itself.
  disableAutoSelect();

  const current = readSession();
  markSignedOut();
  await bridge.markSignedOut();
  await bridge.scheduleNotifications([]);
  await bridge.writeSnapshot({ signedOut: true });
  try {
    if (current !== null) await signOutOfBackend(current.token);
  } catch (cause) {
    // Server logout is best-effort: offline sign-out still clears this device.
    // Storage cleanup below is mandatory and rejects on failure (INV-7).
    console.warn(
      "Could not notify the server of sign-out; signing out locally anyway:",
      cause instanceof Error ? cause.message : cause,
    );
  }

  // Recorded BEFORE the wipe, and cleared BY it — `clearLocalData` empties
  // `meta` too, and does it in one all-or-nothing transaction. So this key is
  // present afterwards precisely when the wipe did not happen, which is the one
  // case `resolveDeviceOwner` must not mistake for a live ledger.
  try {
    await setMeta(SIGNED_OUT_KEY, "1");
  } catch (cause) {
    // Not fatal: if `meta` is unwritable the wipe below will almost certainly
    // fail too, and that path already reports itself.
    console.warn(
      "Could not record the sign-out marker:",
      cause instanceof Error ? cause.message : cause,
    );
  }

  // Local state goes regardless of what the server said. A device that still
  // holds a ledger after the user pressed sign out is worse than a stale session
  // row on the server.
  try {
    await clearDevice();
  } catch (cause) {
    throw new Error("Sign-out could not clear this device. It will remain locked out until cleanup succeeds. Please retry.", { cause });
  }
}

/**
 * Finish a sign-out whose wipe failed, if one is owed. Returns true if it ran.
 *
 * The localStorage barrier survives either store's failed wipe. Retrying must
 * finish the native wipe before clearing the ledger and releasing that barrier.
 *
 * Throwing is deliberate. If the retry fails too, the caller's failure path is
 * the honest answer — signed-out at boot, a visible error at sign-in — because
 * the alternatives are adopting a session with the marker still armed (a silent
 * wipe later) or clearing the marker without the wipe (the previous user's
 * ledger left readable, and pushable, under the next session).
 */
async function completeOwedSignOut(): Promise<boolean> {
  if (!signOutPending() && (await getMeta(SIGNED_OUT_KEY)) === null) return false;
  await clearDevice();
  return true;
}

/**
 * Who this device's ledger belongs to at boot, or `null` if nobody.
 *
 * Not simply `localLedgerAccountId()`, because a sign-out whose wipe failed
 * leaves rows behind. Treating those as a live ledger would reopen the app on
 * the ledger of the person who just signed out — on a shared device, exactly the
 * disclosure the sign-out wipe exists to prevent, and a regression against the
 * behaviour where a tokenless boot always meant the sign-in screen.
 *
 * So a surviving marker means "finish what the sign-out started": retry the
 * wipe, and report no owner either way. If the retry throws, the caller's
 * failure path lands on signed-out, which is the safe direction.
 */
export async function resolveDeviceOwner(): Promise<string | null> {
  if (await completeOwedSignOut()) return null;
  return localLedgerAccountId();
}

export type AdoptOutcome =
  | { kind: "adopted" }
  /**
   * A different account's ledger is here with changes it never uploaded, and
   * its token is already dead — so they cannot be pushed under it, and pushing
   * them under the incoming one would file them in the wrong account. The
   * caller asks: sign in as `previousEmail` to upload, or discard.
   * `previousEmail` is null for a ledger written before the label was kept.
   */
  | { kind: "blocked"; pending: number; previousEmail: string | null; reason?: "family-unknown" };

/**
 * Take on a freshly issued session, deciding what happens to the ledger
 * already on the device. **Every sign-in passes through here**, first or
 * re-authentication. A `blocked` result means **nothing happened at all**.
 *
 * The caller applies the session; this function only decides the ledger's fate.
 *
 * **Switching accounts is upload, clear, load — and never a merge.** With
 * nothing pending the other account's rows are all on the server already, so
 * the device clears and the new account loads without a question. With
 * something pending it blocks, unless the user has already chosen to discard.
 *
 * **Why the comparison exists at all.** The local database is one un-partitioned
 * set of tables (`localLedgerAccountId`), and `pushSpec` uploads every dirty row
 * it finds under whatever bearer token it is holding. Signing in as a different
 * account over an existing ledger would therefore push one person's expenses
 * into another person's account. Two accounts' rows never meet on one device.
 */
export async function adoptReauthenticatedSession(
  session: Session,
  options: { discard?: boolean } = {},
): Promise<AdoptOutcome> {
  // Before anything else: a sign-out whose wipe failed left rows here AND the
  // marker that records the debt. Adopting over them without settling it is the
  // one way the marker survives a sign-in — and `resolveDeviceOwner` acts on it
  // at the *next tokenless boot*, which `readSession()` guarantees will arrive
  // when the session TTL passes. That would wipe the ledger, weeks of new
  // expenses included, with no prompt and no explanation. Nothing is lost that
  // was not already consented to: the user chose to sign out.
  if (!(await completeOwedSignOut())) {
    const owner = await localLedgerAccountId();

    // Somebody else's ledger. Nothing here or already theirs falls straight
    // through and keeps the ledger — the case the re-authentication exists for:
    // a revoked session must not cost the user what they logged while it died.
    if (owner !== null && owner !== session.accountId) {
      let pending: number;
      try { pending = await countAllPending(); }
      catch (cause) {
        if (!(cause instanceof UnknownFamilyPending)) throw cause;
        if (options.discard !== true) return { kind: "blocked", pending: cause.personalPending, previousEmail: await getMeta(LEDGER_OWNER_EMAIL_KEY), reason: "family-unknown" };
        pending = cause.personalPending;
      }
      if (pending > 0 && options.discard !== true) {
        return { kind: "blocked", pending, previousEmail: await getMeta(LEDGER_OWNER_EMAIL_KEY) };
      }
      await clearDevice();
    }
  }

  // Refreshed on every adoption: the email is a label that can change under
  // the same `google_sub`, and the sheet should name the account as it is now.
  await setMeta(LEDGER_OWNER_EMAIL_KEY, session.email);
  return { kind: "adopted" };
}


class UnknownFamilyPending extends Error {
  readonly personalPending: number;
  constructor(personalPending: number, cause: unknown) { super("Family changes may remain on this device, but could not be counted.", { cause }); this.personalPending = personalPending; }
}
async function countAllPending(): Promise<number> {
  const personal = await countPending();
  // Membership is cached in the personal store before any family rows are loaded.
  const known = await getMeta(FAMILY_ID_KEY);
  if (!known) return personal;
  try { return personal + await countPending(familyLedger(known)); }
  catch (cause) {
    console.warn("Could not count pending family changes:", cause);
    throw new UnknownFamilyPending(personal, cause);
  }
}
export interface RemovedFamilyRows {
  familyId: string;
  rows: { table: string; row: object }[];
}
/** Kept in IndexedDB across restarts, until the future UI makes an explicit choice. */
export async function getRemovedFamilyRows(): Promise<RemovedFamilyRows | null> {
  const removed = await familyDb.meta.get(FAMILY_REMOVED_KEY);
  if (!removed) return null;
  const rows: RemovedFamilyRows["rows"] = [];
  for (const table of [familyDb.profile, familyDb.categories, familyDb.budgets, familyDb.recurringRules, familyDb.expenses]) {
    for (const row of await table.where("_dirty").equals(1).toArray()) rows.push({ table: table.name, row });
  }
  return { familyId: removed.value, rows };
}
async function clearFamilyOutbox(): Promise<void> {
  if (!bridge.hasOutbox) return;
  await bridge.outboxClearLedger("family");
}
/** Copy only live unsent expenses, with stable ids so an interrupted choice can retry. */
export async function resolveRemovedFamilyRows(choice: "copy" | "discard"): Promise<{ copied: number; discarded: Record<string, number> }> {
  const removed = await getRemovedFamilyRows();
  if (!removed) throw new Error("There is no removed family ledger to resolve.");
  let copied = 0;
  const discarded: Record<string, number> = {};
  for (const entry of removed.rows) {
    if (choice === "discard" || entry.table !== "expenses" || (entry.row as LocalExpense).deletedAt !== null) {
      discarded[entry.table] = (discarded[entry.table] ?? 0) + 1;
    }
  }
  if (choice === "copy") {
    const personalCategories = await db.categories.filter(c => c.deletedAt === null && c.archivedAt === null).toArray();
    const familyCategories = await familyDb.categories.toArray();
    const userId = requireUser();
    let fallback = await db.categories.filter(c => c.isFallback && c.deletedAt === null).first();
    if (!fallback) {
      const category = await addCategory(FALLBACK_CATEGORY.name, FALLBACK_CATEGORY.color);
      await db.categories.update(category.id, { isFallback: true, updatedAt: nextUpdatedAt(category.updatedAt) });
      fallback = await db.categories.get(category.id);
    }
    if (!fallback) throw new Error("Could not create a personal fallback category.");
    const fallbackId = fallback.id;
    await db.transaction("rw", db.expenses, async () => {
      for (const entry of removed.rows) {
        if (entry.table !== "expenses") continue;
        const expense = entry.row as LocalExpense;
        if (expense.deletedAt !== null) continue;
        const id = `family-copy:${encodeURIComponent(removed.familyId)}:${expense.id}`;
        copied += 1;
        if (await db.expenses.get(id)) continue;
        const familyCategory = familyCategories.find(c => c.id === expense.categoryId);
        const categoryId = personalCategories.find(c => c.name === familyCategory?.name)?.id ?? fallbackId;
        const now = nowIso();
        await db.expenses.put({ id, userId, categoryId, amountCents: expense.amountCents,
          occurredAt: expense.occurredAt, note: expense.note, recurringRuleId: null, sourceId: null,
          createdAt: now, updatedAt: now, deletedAt: null, _dirty: 1, _syncedAt: null });
      }
    });
  }
  await clearFamilyOutbox();
  await clearDatabase(familyDb);
  await clearGridOrderIfSet();
  await db.meta.delete(FAMILY_VIEW_KEY); // invariant-exempt: INV-5 — local membership display cache
  await db.meta.delete(FAMILY_ID_KEY); // invariant-exempt: INV-5 — local membership cache, never a synced row
  resetFamilySyncStatus();
  if (choice === "copy") queueSync();
  return { copied, discarded };
}
/** Upload first; a failure or held row leaves both membership and local data intact. */
export async function leaveFamily(confirmDelete = false, options: { discard?: boolean } = {}): Promise<LeaveOutcome | { kind: "removed"; pending: number }> {
  if (options.discard && !navigator.onLine) return { kind: "blocked", pending: await countPending(familyLedger("")), reason: "offline" };
  const outcome = options.discard ? { kind: "clear" as const } : await prepareToLeave({ ...defaultLeaveDeps,
    countPending: () => countPending(familyLedger("")), sync: sync, syncState: () => getFamilySyncStatus().state });
  if (outcome.kind === "blocked") return outcome;
  const resume = suspendFamilySync();
  try {
    if (!(await settlesWithin(waitForFamilySync(), SIGN_OUT_UPLOAD_TIMEOUT_MS))) {
      return { kind: "blocked", pending: await countPending(familyLedger("")), reason: "failed" };
    }
    const familyId = (await getMeta(FAMILY_ID_KEY)) ?? await localLedgerAccountId(familyDb);
    await leaveFamilyMembership(confirmDelete);
    await clearGridOrderIfSet();
    let nativePending = 0;
    if (bridge.hasOutbox && !options.discard) {
      const entries = (await bridge.outboxAll()).filter(entry => entry.ledger === "family");
      nativePending = entries.length;
      await importOutboxEntries(entries, readSession()?.accountId ?? "", {
        all: () => bridge.outboxAll(), put: entries => bridge.outboxPut(entries),
        remove: keys => bridge.outboxRemove(keys), ack: acks => bridge.outboxAck(acks), clear: () => bridge.outboxClear(),
      }, familyId);
    }
    if (options.discard) await clearFamilyOutbox();
    const pending = await familyDb.transaction("rw", familyDb.tables, async () => {
      const pending = Math.max(nativePending, await countPending(familyLedger(familyId ?? "")));
      if (pending > 0 && !options.discard) await familyDb.meta.put({ key: FAMILY_REMOVED_KEY, value: familyId ?? "" });
      else await clearDatabase(familyDb);
      return pending;
    });
    if (pending > 0 && !options.discard) return { kind: "removed", pending };
    await clearFamilyOutbox();
    await db.meta.delete(FAMILY_VIEW_KEY); // invariant-exempt: INV-5 — local membership display cache
    await db.meta.delete(FAMILY_ID_KEY); // invariant-exempt: INV-5 — local membership cache, never a synced row
    resetFamilySyncStatus();
    return { kind: "clear" };
  } finally { resume(); }
}
